import React, { useState, useEffect, useMemo } from 'react';
import { RunPaceZone } from '../../types/athlete';
import { Clock, TrendingUp, Info } from 'lucide-react';

interface RunPaceInputProps {
  value: {
    distance: number;
    distanceUnit: 'km' | 'mi';
    timeMinutes: number;
    timeSeconds: number;
  };
  onChange: (value: {
    distance: number;
    distanceUnit: 'km' | 'mi';
    timeMinutes: number;
    timeSeconds: number;
  }) => void;
  showZones?: boolean;
}

const commonDistances = [
  { label: '400m', value: 0.4, unit: 'km' },
  { label: '1 km', value: 1, unit: 'km' },
  { label: '1 mile', value: 1, unit: 'mi' },
  { label: '5K', value: 5, unit: 'km' },
  { label: '10K', value: 10, unit: 'km' },
  { label: 'Half Marathon', value: 21.1, unit: 'km' },
  { label: 'Marathon', value: 42.2, unit: 'km' },
];

const RunPaceInput: React.FC<RunPaceInputProps> = ({
  value,
  onChange,
  showZones = true,
}) => {
  const [zones, setZones] = useState<RunPaceZone[]>([]);

  const paceSecondsPerKm = useMemo(() => {
    const totalSeconds = (value.timeMinutes * 60) + value.timeSeconds;
    const distanceInKm = value.distanceUnit === 'mi' ? value.distance * 1.60934 : value.distance;
    return distanceInKm > 0 ? totalSeconds / distanceInKm : 0;
  }, [value.distance, value.distanceUnit, value.timeMinutes, value.timeSeconds]);

  const formatPace = (secondsPerKm: number): string => {
    if (secondsPerKm === 0 || !isFinite(secondsPerKm)) return '--:--';
    const mins = Math.floor(secondsPerKm / 60);
    const secs = Math.round(secondsPerKm % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (paceSecondsPerKm === 0) {
      setZones([]);
      return;
    }

    const zones: RunPaceZone[] = [
      {
        name: 'Easy / Recovery',
        perKm: formatPace(paceSecondsPerKm * 1.25),
        perMile: formatPace(paceSecondsPerKm * 1.25 * 1.60934),
        description: 'Comfortable, conversational pace',
      },
      {
        name: 'Long Run',
        perKm: formatPace(paceSecondsPerKm * 1.15),
        perMile: formatPace(paceSecondsPerKm * 1.15 * 1.60934),
        description: 'Sustained effort for endurance',
      },
      {
        name: 'Marathon',
        perKm: formatPace(paceSecondsPerKm * 1.05),
        perMile: formatPace(paceSecondsPerKm * 1.05 * 1.60934),
        description: 'Race pace for marathon',
      },
      {
        name: 'Threshold (T)',
        perKm: formatPace(paceSecondsPerKm),
        perMile: formatPace(paceSecondsPerKm * 1.60934),
        description: 'Comfortably hard (~60 min)',
      },
      {
        name: 'Interval (I)',
        perKm: formatPace(paceSecondsPerKm * 0.95),
        perMile: formatPace(paceSecondsPerKm * 0.95 * 1.60934),
        description: 'VO2max effort (3-5 min reps)',
      },
      {
        name: 'Repetition (R)',
        perKm: formatPace(paceSecondsPerKm * 0.88),
        perMile: formatPace(paceSecondsPerKm * 0.88 * 1.60934),
        description: 'Very fast with full recovery',
      },
    ];

    setZones(zones);
  }, [paceSecondsPerKm]);

  const handleDistanceChange = (val: number) => {
    onChange({ ...value, distance: val });
  };

  const handleDistanceUnitChange = (unit: 'km' | 'mi') => {
    onChange({ ...value, distanceUnit: unit });
  };

  const handleMinutesChange = (val: string) => {
    const num = parseInt(val) || 0;
    onChange({ ...value, timeMinutes: Math.max(0, num) });
  };

  const handleSecondsChange = (val: string) => {
    const num = parseInt(val) || 0;
    onChange({ ...value, timeSeconds: Math.max(0, Math.min(59, num)) });
  };

  return (
    <div className="space-y-6">
      <div className="card-base p-6">
        <h3 className="font-semibold mb-4 flex items-center">
          <Clock size={18} className="mr-2 text-[#3b82f6]" />
          Reference Run Performance
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
              Distance
            </label>
            <div className="flex space-x-2">
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={value.distance}
                onChange={(e) => handleDistanceChange(parseFloat(e.target.value) || 0)}
                className="flex-1"
                placeholder="5"
              />
              <select
                value={value.distanceUnit}
                onChange={(e) => handleDistanceUnitChange(e.target.value as 'km' | 'mi')}
                className="w-24"
              >
                <option value="km">km</option>
                <option value="mi">miles</option>
              </select>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              {commonDistances.map((dist) => (
                <button
                  key={dist.label}
                  type="button"
                  onClick={() => {
                    onChange({
                      ...value,
                      distance: dist.value,
                      distanceUnit: dist.unit as 'km' | 'mi',
                    });
                  }}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    value.distance === dist.value && value.distanceUnit === dist.unit
                      ? 'bg-[#3b82f6] text-white'
                      : 'bg-[#141921] text-[#718096] hover:bg-[#2d3748]'
                  }`}
                >
                  {dist.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
              Time
            </label>
            <div className="flex space-x-2">
              <div className="flex-1">
                <input
                  type="number"
                  min="0"
                  value={value.timeMinutes}
                  onChange={(e) => handleMinutesChange(e.target.value)}
                  className="w-full"
                  placeholder="20"
                />
                <span className="text-xs text-muted">minutes</span>
              </div>
              <span className="flex items-center text-muted">:</span>
              <div className="flex-1">
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={value.timeSeconds}
                  onChange={(e) => handleSecondsChange(e.target.value)}
                  className="w-full"
                  placeholder="00"
                />
                <span className="text-xs text-muted">seconds</span>
              </div>
            </div>
          </div>
        </div>

        {paceSecondsPerKm > 0 && (
          <div className="mt-4 p-3 bg-[#0a0e14] rounded-lg">
            <p className="text-sm text-muted">
              Your baseline pace: <span className="font-medium text-[#3b82f6]">{formatPace(paceSecondsPerKm)}/km</span>
            </p>
          </div>
        )}
      </div>

      {showZones && zones.length > 0 && (
        <div className="card-base p-6">
          <h3 className="font-semibold mb-4 flex items-center">
            <TrendingUp size={18} className="mr-2 text-[#3b82f6]" />
            Calculated Pace Zones
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {zones.map((zone) => (
              <div key={zone.name} className="p-3 bg-[#0a0e14] rounded-lg">
                <p className="font-medium text-sm text-[#3b82f6]">{zone.name}</p>
                <p className="text-lg font-bold">{zone.perKm}/km</p>
                <p className="text-xs text-muted">{zone.perMile}/mi</p>
                <p className="text-xs text-muted mt-1">{zone.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-start space-x-2 p-3 bg-[rgba(59,130,246,0.1)] border border-[rgba(59,130,246,0.2)] rounded-lg">
        <Info size={16} className="text-[#3b82f6] mt-0.5" />
        <p className="text-sm text-[#e2e8f0]">
          Enter a recent race or timed run. Your pace zones will be calculated automatically based on this performance.
        </p>
      </div>
    </div>
  );
};

export default RunPaceInput;
